import { useEffect, useRef, useState } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { api, ApiError, fetchFileBlobUrl, fileToUpload } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Logo } from './Shell';
import { Button, useToast } from './ui';

/**
 * One fetch per logo, for the life of the tab.
 *
 * The mark sits in a header that re-renders constantly and remounts on every
 * navigation. Without this, every one of those would be a round trip to Apps
 * Script for the same handful of kilobytes — and Apps Script is not fast.
 */
const cache = new Map<string, Promise<string>>();

function logoUrl(fileId: string): Promise<string> {
  const hit = cache.get(fileId);
  if (hit) return hit;
  const pending = fetchFileBlobUrl(fileId);
  cache.set(fileId, pending);
  // A failed load must not be remembered as a failure forever — a tenant on a
  // dropped connection would then never see the logo again until they reload.
  pending.catch(() => cache.delete(fileId));
  return pending;
}

/**
 * The organisation's own mark, falling back to PROVIA's.
 *
 * The fallback is the point: most accounts will never upload one, and a header
 * with a hole in it where a logo should be looks broken in a way that a
 * perfectly good default does not.
 */
export function OrgLogo({ fileId, className }: { fileId?: string | null; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setUrl(null);
    if (!fileId) return undefined;
    logoUrl(fileId).then((u) => { if (live) setUrl(u); }).catch(() => {});
    return () => { live = false; };
    /*
     * The object URL is deliberately not revoked here. It is shared by every
     * mount through the cache above, and revoking it when one header unmounts
     * would break the next one. They are released when the tab closes.
     */
  }, [fileId]);

  if (!fileId || !url) return <Logo className={className} />;
  return (
    <img
      src={url}
      alt=""
      className={`${className ?? 'h-8 w-8'} shrink-0 rounded-lg object-contain bg-raised`}
    />
  );
}

/**
 * Uploading it, in Settings.
 *
 * A company and a person get the same control. Somebody letting two flats
 * under a name they chose is not less entitled to look like themselves than a
 * limited company is — and the tenant reading the portal cannot tell the
 * difference anyway.
 */
export function LogoSettings({ canEdit }: { canEdit: boolean }) {
  const { staff, refresh } = useAuth();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const fileId = staff?.org.logo_file_id ?? null;

  async function upload(file: File) {
    setBusy(true);
    try {
      /*
       * PNG is kept as PNG when it is small enough, rather than run through
       * the shared downscaler — that re-encodes to JPEG, and a JPEG cannot
       * hold transparency. A logo with a white box around it on a dark header
       * is exactly the sort of thing somebody notices and we would not.
       */
      const payload = file.type === 'image/png' && file.size < 1_000_000
        ? {
          filename: file.name,
          mime_type: file.type,
          data: await fileToBase64(file),
        }
        : await fileToUpload(file, { maxDimension: 512, quality: 0.9 });

      const res = await api.post('/settings/logo', payload);
      cache.delete(res.logo_file_id);
      await refresh();
      toast.success('Logo updated');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not upload that image');
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await api.post('/settings/logo', { remove: true });
      await refresh();
      toast.success('Logo removed');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove the logo');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex items-center gap-4 flex-wrap">
      <OrgLogo fileId={fileId} className="h-14 w-14" />
      <div className="flex-1 min-w-[14rem]">
        <p className="text-sm text-muted leading-relaxed">
          Shown to your tenants at the top of their portal and on anything you print or
          send them. PNG keeps a transparent background; anything else is resized to fit.
        </p>
      </div>
      {canEdit && (
        <div className="flex items-center gap-2">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
          <Button
            size="sm"
            loading={busy}
            icon={<Upload className="h-3.5 w-3.5" />}
            onClick={() => input.current?.click()}
          >
            {fileId ? 'Replace' : 'Upload'}
          </Button>
          {fileId && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={() => void remove()}
            >
              Remove
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}
